import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  LinearProgress,
  Chip,
  Stack,
  Divider,
  Tabs,
  Tab,
  Tooltip,
  Collapse,
  IconButton,
  Alert,
} from '@mui/material';
import {
  Home,
  PlayArrow,
  Stop,
  Sync,
  Visibility,
  ExpandMore,
  ExpandLess,
  CloudDone,
  CloudOff,
  TrendingUp,
  Assessment,
  Fullscreen,
  FullscreenExit,
} from '@mui/icons-material';
import UsersList from '../../components/Users/UsersList.tsx';
import { API_BASE_URL } from '../../constants.ts';

type HomeValuationsStats = {
  totalJobs: number;
  completedJobs: number;
  bofaValued: number;
  movotoValued: number;
};

type CategoryStats = { totalJobs: number; completedJobs: number };

type ProgressStats = {
  privyFound: number;
  homeValuations: HomeValuationsStats;
  privy: CategoryStats;
  currentListings: CategoryStats;
  // Optional extras the backend might add later; keep optional for safety
  homesFound?: number;
  notMeetSpread?: number;
};

type ProgressTracker = {
  isRunning: boolean;
  status: 'idle' | 'running' | 'completed' | 'error' | string;
  lastRun: string | null;
  jobs: string[];
  error?: string | null;
  stats: ProgressStats;
};

const defaultStats: ProgressStats = {
  privyFound: 0,
  homeValuations: { totalJobs: 0, completedJobs: 0, bofaValued: 0, movotoValued: 0 },
  privy: { totalJobs: 0, completedJobs: 0 },
  currentListings: { totalJobs: 0, completedJobs: 0 },
  homesFound: 0,
  notMeetSpread: 0,
};

const formatNumber = (n?: number) => (typeof n === 'number' ? n.toLocaleString('en-US') : '0');
const pct = (done: number, total: number) => (total > 0 ? Math.min(100, Math.round((done / total) * 1000) / 10) : 0);
const fmtDateTime = (s?: string | null) => (s ? new Date(s).toLocaleString() : '—');
const formatLogLine = (data: any): string => {
  const timeStr = new Date(data?.ts || Date.now()).toLocaleTimeString();
  // Treat bare progressTracker snapshots like 'status' events
  if (!data?.type && (data?.stats || typeof data?.isRunning === 'boolean' || typeof data?.status === 'string')) {
    const s = data?.stats || {};
    const hv = s.homeValuations || {};
    const pv = s.privy || {};
    const cl = s.currentListings || {};
    const total = (pv.totalJobs || 0) + (hv.totalJobs || 0) + (cl.totalJobs || 0);
    const done = (pv.completedJobs || 0) + (hv.completedJobs || 0) + (cl.completedJobs || 0);
    const percent = (total ? (done / total) * 100 : 0).toFixed(1);
    return `[${timeStr}] ${String(data.status || 'status').toUpperCase()} | overall ${percent}% | BofA ${hv.completedJobs || 0}/${hv.totalJobs || 0} (B:${hv.bofaValued || 0} M:${hv.movotoValued || 0}) | Privy ${pv.completedJobs || 0}/${pv.totalJobs || 0} | Homes ${cl.completedJobs || 0}/${cl.totalJobs || 0}`;
  }
  if (data?.type === 'update' || data?.type === 'status') {
    const s = data?.stats || {};
    const hv = s.homeValuations || {};
    const pv = s.privy || {};
    const cl = s.currentListings || {};
    const total = (pv.totalJobs || 0) + (hv.totalJobs || 0) + (cl.totalJobs || 0);
    const done = (pv.completedJobs || 0) + (hv.completedJobs || 0) + (cl.completedJobs || 0);
    const percent = (total ? (done / total) * 100 : 0).toFixed(1);
    return `[${timeStr}] ${String(data.status || 'update').toUpperCase()} | overall ${percent}% | BofA ${hv.completedJobs || 0}/${hv.totalJobs || 0} (B:${hv.bofaValued || 0} M:${hv.movotoValued || 0}) | Privy ${pv.completedJobs || 0}/${pv.totalJobs || 0} | Homes ${cl.completedJobs || 0}/${cl.totalJobs || 0}`;
  }
  if (data?.type === 'heartbeat') return `[${timeStr}] heartbeat (${data.status || 'running'})`;
  if (data?.type === 'log') {
    const lvl = (data.level || 'info').toUpperCase();
    const scope = data.scope ? `[${data.scope}]` : '';
    const icon = data.icon || '';
    const extra = data.fields && Object.keys(data.fields).length ? ' ' + JSON.stringify(data.fields) : '';
    return `[${timeStr}] ${icon} ${lvl} ${scope} ${data.msg || ''}${extra}`.trim();
  }
  return `[${timeStr}] ${data?.message || data?.status || 'event'}`;
};

const StatusChip: React.FC<{ status: ProgressTracker['status']; running: boolean }> = ({ status, running }) => {
  const map: Record<string, { label: string; color: 'default' | 'success' | 'warning' | 'error' | 'info'; icon: React.ReactNode }> = {
    running: { label: 'Running', color: 'success', icon: <CloudDone fontSize="small" /> },
    idle: { label: 'Idle', color: 'default', icon: <CloudOff fontSize="small" /> },
    completed: { label: 'Completed', color: 'info', icon: <Assessment fontSize="small" /> },
    error: { label: 'Error', color: 'error', icon: <Assessment fontSize="small" /> },
  };
  const v = map[status] || (running ? map.running : map.idle);
  return <Chip size="small" color={v.color} icon={v.icon} label={v.label} />;
};

const ControlPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusErr, setStatusErr] = useState<string | null>(null);
  const [expandedRaw, setExpandedRaw] = useState(false);
  const [tracker, setTracker] = useState<ProgressTracker>({
    isRunning: false,
    status: 'idle',
    lastRun: null,
    jobs: [],
    error: null,
    stats: defaultStats,
  });
  const [logsOpen, setLogsOpen] = useState(false);
  const [logsFullscreen, setLogsFullscreen] = useState(false);
  const [logLines, setLogLines] = useState<string[]>([]);
  const lastGoodLogURL = useRef<string | null>(null);
  const logBoxRef = useRef<HTMLDivElement | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      setStatusErr(null);
      const res = await fetch(`${API_BASE_URL}/api/automation/status`, { credentials: 'omit' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as ProgressTracker;
      // Defensive fill
      data.stats = { ...defaultStats, ...(data.stats || {}) };
      setTracker(data);
    } catch (e: any) {
      setStatusErr(e?.message || 'Failed to fetch status');
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, 3000);
    return () => clearInterval(id);
  }, [fetchStatus]);

  const overall = useMemo(() => {
    const s = tracker.stats || defaultStats;
    const total =
      (s.privy?.totalJobs || 0) +
      (s.homeValuations?.totalJobs || 0) +
      (s.currentListings?.totalJobs || 0);
    const done =
      (s.privy?.completedJobs || 0) +
      (s.homeValuations?.completedJobs || 0) +
      (s.currentListings?.completedJobs || 0);
    return { total, done, percent: pct(done, total) };
  }, [tracker]);

  useEffect(() => {
    if (logsOpen && logBoxRef.current) {
      logBoxRef.current.scrollTop = logBoxRef.current.scrollHeight;
    }
  }, [logLines, logsOpen])

  useEffect(() => {
    if (!logsOpen) return;
    // Clear old lines when (re)opening
    setLogLines([]);

    const primary = `${API_BASE_URL}/api/automation/logs`;
    const fallback = `${API_BASE_URL}/api/automation/status/logs`;
    const tryURLs = lastGoodLogURL.current ? [lastGoodLogURL.current, primary, fallback] : [primary, fallback];

    let es: EventSource | null = null;
    let tried = 0;

    const openNext = () => {
      if (es) {
        try { es.close(); } catch {}
        es = null;
      }
      if (tried >= tryURLs.length) {
        setLogLines((prev) => [...prev, `[${new Date().toLocaleTimeString()}] (error) could not open SSE stream`]);
        return;
      }
      const url = tryURLs[tried++];
      es = new EventSource(url);
      let gotAny = false;

      es.onmessage = (ev) => {
        gotAny = true;
        lastGoodLogURL.current = url;
        try {
          const data = JSON.parse(ev.data);
          if (data?.type === 'logs' && Array.isArray(data.logs)) {
            setLogLines((prev) => {
              const appended = data.logs.map((l: any) => formatLogLine({ type: 'log', ...l }));
              const next = prev.concat(appended);
              return next.length > 1000 ? next.slice(-800) : next;
            });
            return;
          }
          if (data?.type === 'log') {
            const line = formatLogLine(data);
            setLogLines((prev) => {
              const next = prev.length > 800 ? prev.slice(-600) : prev;
              return [...next, line];
            });
            return;
          }
          const line = formatLogLine(data);
          setLogLines((prev) => {
            const next = prev.length > 800 ? prev.slice(-600) : prev;
            return [...next, line];
          });
        } catch {
          setLogLines((prev) => [...prev, `[${new Date().toLocaleTimeString()}] (raw) ${ev.data}`]);
        }
      };

      es.onerror = () => {
        if (!gotAny) {
          // Try next URL if we never received a message
          openNext();
        } else {
          // If it once worked, show a disconnect notice and let EventSource auto-retry
          setLogLines((prev) => [...prev, `[${new Date().toLocaleTimeString()}] (error) stream disconnected`]);
        }
      };
    };

    openNext();

    return () => {
      if (es) {
        try { es.close(); } catch {}
      }
    };
  }, [logsOpen]);

  // When logsOpen is closed, reset logsFullscreen to false
  useEffect(() => {
    if (!logsOpen && logsFullscreen) {
      setLogsFullscreen(false);
    }
  }, [logsOpen, logsFullscreen]);

  const start = async () => {
    setLoading(true);
    try {
      // Prefer explicit start endpoint; fallback to /run if your backend uses that
      const res = await fetch(`${API_BASE_URL}/api/automation/start`, { method: 'POST' });
      if (!res.ok) {
        // try fallback
        const alt = await fetch(`${API_BASE_URL}/api/automation/run`, { method: 'POST' });
        if (!alt.ok) throw new Error(`Start failed (HTTP ${alt.status})`);
      }
      await fetchStatus();
    } catch (e: any) {
      setStatusErr(e?.message || 'Start failed');
    } finally {
      setLoading(false);
    }
  };

  const stop = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/automation/stop`, { method: 'POST' });
      if (!res.ok) throw new Error(`Stop failed (HTTP ${res.status})`);
      await fetchStatus();
    } catch (e: any) {
      setStatusErr(e?.message || 'Stop failed');
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    setLoading(true);
    try {
      await fetchStatus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.900', color: 'white', p: 4 }}>
      <Box mb={4}>
        <Typography
          variant="h4"
          component="h1"
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}
          textAlign="center"
        >
          <Home sx={{ color: 'primary.main' }} />
          Automation Control Panel
        </Typography>
        <Typography variant="body1" sx={{ color: 'grey.400' }} textAlign="center">
          Start/stop automations, monitor progress, and view live stats
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} textColor="inherit" indicatorColor="primary" centered>
          <Tab label="Automation" />
          <Tab label="Users" />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <Stack spacing={3}>
          {statusErr && <Alert severity="error">Status error: {statusErr}</Alert>}

          {/* Header Controls */}
          <Card sx={{ bgcolor: 'grey.800' }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <StatusChip status={tracker.status} running={tracker.isRunning} />
                    <Typography variant="body2" sx={{ color: 'grey.400' }}>
                      Last run: {fmtDateTime(tracker.lastRun)}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<PlayArrow />}
                      onClick={start}
                      disabled={loading || tracker.isRunning}
                    >
                      Start
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<Stop />}
                      onClick={stop}
                      disabled={loading || !tracker.isRunning}
                    >
                      Stop
                    </Button>
                    <Button
                      variant="text"
                      color="info"
                      startIcon={<Sync />}
                      onClick={refresh}
                      disabled={loading}
                    >
                      Refresh
                    </Button>
                    <Button
                      variant={logsOpen ? 'contained' : 'outlined'}
                      color="secondary"
                      startIcon={<Visibility />}
                      onClick={() => setLogsOpen((v) => !v)}
                      disabled={loading}
                    >
                      {logsOpen ? 'Hide logs' : 'Show logs'}
                    </Button>
                    <Tooltip title="Show raw tracker JSON">
                      <IconButton onClick={() => setExpandedRaw((s) => !s)} color="inherit">
                        {expandedRaw ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Grid>
              </Grid>

              {/* Overall Progress */}
              <Box mt={3}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUp fontSize="small" />
                    Overall Progress
                  </Typography>
                  <Chip size="small" label={`${overall.percent.toFixed(1)}%`} />
                  <Typography variant="body2" sx={{ color: 'grey.400' }}>
                    ({formatNumber(overall.done)} / {formatNumber(overall.total)})
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={overall.percent}
                  sx={{ mt: 1, height: 10, borderRadius: 1, bgcolor: 'grey.700' }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Stats Summary */}
          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12} md={4}>
              <Card sx={{ bgcolor: 'grey.800', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="subtitle1">Privy</Typography>
                  <Typography variant="body2" sx={{ color: 'grey.400' }}>
                    Properties found: <strong>{formatNumber(tracker.stats?.privyFound)}</strong>
                  </Typography>
                  <Box mt={1}>
                    <LinearProgress
                      variant="determinate"
                      value={pct(tracker.stats?.privy?.completedJobs || 0, tracker.stats?.privy?.totalJobs || 0)}
                      sx={{ height: 8, borderRadius: 1, bgcolor: 'grey.700' }}
                    />
                    <Typography variant="caption" sx={{ color: 'grey.400' }}>
                      {formatNumber(tracker.stats?.privy?.completedJobs || 0)} / {formatNumber(tracker.stats?.privy?.totalJobs || 0)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ bgcolor: 'grey.800', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="subtitle1">Home Valuations</Typography>
                  <Typography variant="body2" sx={{ color: 'grey.400' }}>
                    BofA valued: <strong>{formatNumber(tracker.stats?.homeValuations?.bofaValued)}</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'grey.400' }}>
                    Movoto valued: <strong>{formatNumber(tracker.stats?.homeValuations?.movotoValued)}</strong>
                  </Typography>
                  <Box mt={1}>
                    <LinearProgress
                      variant="determinate"
                      value={pct(tracker.stats?.homeValuations?.completedJobs || 0, tracker.stats?.homeValuations?.totalJobs || 0)}
                      sx={{ height: 8, borderRadius: 1, bgcolor: 'grey.700' }}
                    />
                    <Typography variant="caption" sx={{ color: 'grey.400' }}>
                      {formatNumber(tracker.stats?.homeValuations?.completedJobs || 0)} / {formatNumber(tracker.stats?.homeValuations?.totalJobs || 0)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ bgcolor: 'grey.800', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="subtitle1">Current Listings (Homes.com)</Typography>
                  <Typography variant="body2" sx={{ color: 'grey.400' }}>
                    Properties found: <strong>{formatNumber(tracker.stats?.homesFound || tracker.stats?.currentListings?.completedJobs || 0)}</strong>
                  </Typography>
                  <Box mt={1}>
                    <LinearProgress
                      variant="determinate"
                      value={pct(tracker.stats?.currentListings?.completedJobs || 0, tracker.stats?.currentListings?.totalJobs || 0)}
                      sx={{ height: 8, borderRadius: 1, bgcolor: 'grey.700' }}
                    />
                    <Typography variant="caption" sx={{ color: 'grey.400' }}>
                      {formatNumber(tracker.stats?.currentListings?.completedJobs || 0)} / {formatNumber(tracker.stats?.currentListings?.totalJobs || 0)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Optional: Not-meet-spread and other rollups */}
          <Card sx={{ bgcolor: 'grey.800' }}>
            <CardContent>
              <Typography variant="subtitle1">Quality / Filters</Typography>
              <Stack direction="row" spacing={2} sx={{ mt: 1 }} divider={<Divider orientation="vertical" flexItem />}>
                <Typography variant="body2" sx={{ color: 'grey.400' }}>
                  Not meeting spread / tier: <strong>{formatNumber(tracker.stats?.notMeetSpread || 0)}</strong>
                </Typography>
              </Stack>
            </CardContent>
          </Card>
          <Collapse in={logsOpen} unmountOnExit>
            <Box
              sx={
                logsFullscreen
                  ? {
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      zIndex: 2000,
                      bgcolor: 'black',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      m: 0,
                    }
                  : { mt: 2 }
              }
            >
              <Card
                sx={
                  logsFullscreen
                    ? {
                        width: '90vw',
                        height: '90vh',
                        bgcolor: 'grey.900',
                        display: 'flex',
                        flexDirection: 'column',
                      }
                    : { bgcolor: 'grey.850' as any }
                }
              >
                <CardContent
                  sx={
                    logsFullscreen
                      ? { flex: '1 1 0', display: 'flex', flexDirection: 'column', minHeight: 0, p: 2 }
                      : undefined
                  }
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Visibility fontSize="small" />
                    <Typography variant="subtitle2">Live automation logs</Typography>
                    <Chip size="small" label={`${logLines.length.toLocaleString()} lines`} sx={{ ml: 'auto' }} />
                    <Tooltip title={logsFullscreen ? "Exit fullscreen" : "Fullscreen"}>
                      <IconButton
                        onClick={() => setLogsFullscreen((v) => !v)}
                        color="inherit"
                        size="small"
                        sx={{ ml: 1 }}
                      >
                        {logsFullscreen ? <FullscreenExit /> : <Fullscreen />}
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <Box
                    ref={logBoxRef}
                    component="pre"
                    sx={{
                      fontFamily:
                        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                      fontSize: 12,
                      ...(logsFullscreen
                        ? {
                            flexGrow: 1,
                            height: 0,
                            minHeight: 0,
                            maxHeight: '100%',
                            overflow: 'auto',
                            p: 2,
                            borderRadius: 1,
                            bgcolor: 'black',
                            color: 'rgb(220,220,220)',
                            border: '1px solid rgba(255,255,255,0.1)',
                          }
                        : {
                            maxHeight: 360,
                            overflow: 'auto',
                            p: 2,
                            borderRadius: 1,
                            bgcolor: 'black',
                            color: 'rgb(220,220,220)',
                            border: '1px solid rgba(255,255,255,0.1)',
                          }),
                    }}
                  >
                    {logLines.join('\n')}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Collapse>

          {/* Raw JSON (collapsible) */}
          <Card sx={{ bgcolor: 'grey.850' as any }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Visibility fontSize="small" />
                <Typography variant="subtitle2">Raw progress data</Typography>
                <IconButton onClick={() => setExpandedRaw((s) => !s)} size="small" color="inherit">
                  {expandedRaw ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Stack>
              <Collapse in={expandedRaw} unmountOnExit>
                <Box component="pre" sx={{ bgcolor: 'grey.900', p: 2, borderRadius: 1, overflowX: 'auto' }}>
                  {JSON.stringify(tracker, null, 2)}
                </Box>
              </Collapse>
            </CardContent>
          </Card>
        </Stack>
      )}

      {activeTab === 1 && (
        <Box>
          <UsersList />
        </Box>
      )}
    </Box>
  );
};

export default ControlPanel;